import { commonWordCheck } from './commonWordCheck';

// Custom diff function to replace fast-array-diff
const customDiff = (arr1: string[], arr2: string[]) => {
  const added: string[] = [];
  const removed: string[] = [];

  let i = 0,
    j = 0;

  while (i < arr1.length || j < arr2.length) {
    if (i >= arr1.length) {
      // Remaining characters in arr2 are added
      added.push(...arr2.slice(j));
      break;
    }
    if (j >= arr2.length) {
      // Remaining characters in arr1 are removed
      removed.push(...arr1.slice(i));
      break;
    }

    if (arr1[i] === arr2[j]) {
      // Characters match, move both pointers
      i++;
      j++;
    } else {
      // Look ahead to see if we can find a match
      let foundMatch = false;

      // Check if arr1[i] matches any upcoming character in arr2
      for (let k = j + 1; k < Math.min(j + 3, arr2.length); k++) {
        if (arr1[i] === arr2[k]) {
          // Found match, characters between j and k are added
          added.push(...arr2.slice(j, k));
          j = k;
          foundMatch = true;
          break;
        }
      }

      if (!foundMatch) {
        // Check if arr2[j] matches any upcoming character in arr1
        for (let k = i + 1; k < Math.min(i + 3, arr1.length); k++) {
          if (arr2[j] === arr1[k]) {
            // Found match, characters between i and k are removed
            removed.push(...arr1.slice(i, k));
            i = k;
            foundMatch = true;
            break;
          }
        }
      }

      if (!foundMatch) {
        // No match found in lookahead, treat as substitution
        removed.push(arr1[i]);
        added.push(arr2[j]);
        i++;
        j++;
      }
    }
  }

  return { added, removed };
};

const replacementChecks = [
  { pattern: /(?<!\b)[sz](?!\b)/g, replacement: '' },
  { pattern: /re$/g, replacement: 'er' },
  { pattern: /our/g, replacement: 'or' },
  { pattern: /ogue$/g, replacement: 'og' },
  { pattern: /ae/g, replacement: 'e' },
  { pattern: /oe/g, replacement: 'e' },
  { pattern: /ence$/g, replacement: 'ense' },
  { pattern: /ise$/g, replacement: 'ize' },
  { pattern: /yse$/g, replacement: 'yze' },
  { pattern: /ll(?=ed|ing|er|est)$/g, replacement: 'l' },
  { pattern: /eable$/g, replacement: 'able' },
  { pattern: /aemia/g, replacement: 'emia' },
  { pattern: /aesth/g, replacement: 'esth' },
  { pattern: /leukaem/g, replacement: 'leukem' },
  { pattern: /oestro/g, replacement: 'estro' },
  { pattern: /paedia/g, replacement: 'pedia' },
  { pattern: /aero/g, replacement: 'air' },
  { pattern: /haemo/g, replacement: 'hemo' },
  { pattern: /ce$/g, replacement: 'se' },
  { pattern: /xion/g, replacement: 'ction' },
  { pattern: /gramme$/g, replacement: 'gram' },
  { pattern: /logue$/g, replacement: 'log' },
  { pattern: /eing$/g, replacement: 'ing' },
];

const specialCases = [
  { british: 'grey', american: 'gray' },
  { british: 'aluminium', american: 'aluminum' },
  { british: 'caesium', american: 'cesium' },
  { british: 'manoeuvre', american: 'maneuver' },
  { british: 'cheque', american: 'check' },
  { british: 'programme', american: 'program' },
  { british: 'archaeology', american: 'archeology' },
  { british: 'diarrhoea', american: 'diarrhea' },
  { british: 'encyclopaedia', american: 'encyclopedia' },
  { british: 'faeces', american: 'feces' },
];

const checkReplacement = (word1, word2, { pattern, replacement }) => {
  return word1.replace(pattern, replacement) === word2.replace(pattern, replacement);
};

const checkDoubleConsonants = (word1, word2) => {
  const doubleConsonants = ['bb', 'dd', 'ff', 'll', 'mm', 'nn', 'pp', 'rr', 'tt'];
  for (const cc of doubleConsonants) {
    if (word1.replace(cc, cc[0]) === word2 || word2.replace(cc, cc[0]) === word1) {
      return true;
    }
  }
  return false;
};

const checkSpecialCases = (word1, word2) => {
  return specialCases.some(
    ({ british, american }) =>
      (word1 === british && word2 === american) || (word1 === american && word2 === british)
  );
};

const compareAddRemove = ({ addStr, removeStr, letters = [], word1, word2 }) => {
  const indexAdd = word1.indexOf(removeStr);
  const indexRemove = word2.indexOf(addStr);
  if (indexAdd === -1 || indexRemove === -1) {
    return false;
  }
  if (indexAdd === word1.length - 1 && indexRemove === word2.length - 1) {
    return false;
  }
  if (
    (addStr === letters[0] && removeStr === letters[1] && indexAdd === indexRemove) ||
    (addStr === letters[1] && removeStr === letters[0] && indexAdd === indexRemove)
  ) {
    return true;
  }
  return false;
};

const getWordDiff = (word1, word2) => {
  if (checkSpecialCases(word1, word2)) {
    return true;
  }

  if (checkDoubleConsonants(word1, word2)) {
    return true;
  }

  for (const check of replacementChecks) {
    if (checkReplacement(word1, word2, check)) {
      return true;
    }
  }

  const d = customDiff(word1.split(''), word2.split(''));
  if (d.added.length === 1 && d.removed.length === 1) {
    const addStr = d.added.join('');
    const removeStr = d.removed.join('');

    const commonPairs = [
      ['e', 'i'],
      ['x', 'c'],
      ['u', 'o'],
      ['a', 'e'],
      ['g', 'j'],
    ];

    for (const pair of commonPairs) {
      if (compareAddRemove({ word1, word2, addStr, removeStr, letters: pair })) {
        return true;
      }
    }
  }

  return false;
};

export const checkWordEquality = (word1, word2) => {
  const { equal, word1: lowerWord1, word2: lowerWord2 } = commonWordCheck(word1, word2);
  switch (equal) {
    case 'yes':
      return true;
    case 'no':
      return false;
  }

  return getWordDiff(lowerWord1, lowerWord2);
};
